import React from "react";
import { Link } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import Icon from "@/components/Icon";
import AfricaMap from "@/components/AfricaMap";

export default function Home() {
  const { content, loading } = useContent();
  if (loading || !content) return <PageSkeleton />;
  const h = content.home || {};

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="sp-hero">
        <div className="sp-container relative pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 sp-fade sp-fade-1">
            {h.eyebrow && <span className="sp-eyebrow">{h.eyebrow}</span>}
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">
              {h.headline_top}
              <br />
              <span className="text-gold">{h.headline_bottom}</span>
            </h1>
            {h.subheadline && (
              <p className="mt-6 text-lg sm:text-xl text-navy font-semibold max-w-2xl leading-snug">
                {h.subheadline}
              </p>
            )}
            {h.description && (
              <p className="mt-5 text-[15px] text-slate-600 max-w-xl leading-relaxed">
                {h.description}
              </p>
            )}
            <div className="mt-8 flex flex-wrap gap-3" data-testid="home-hero-ctas">
              <Link to="/contact" className="sp-btn-primary" data-testid="cta-book-strategy">
                <Icon name="Calendar" size={18} /> {h.primary_cta || "Book a Strategy Session"}
              </Link>
              <Link to="/solutions" className="sp-btn-gold" data-testid="cta-explore-solutions">
                {h.secondary_cta || "Explore Our Solutions"} <Icon name="ArrowRight" size={18} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 relative sp-fade sp-fade-2">
            <div className="relative aspect-[4/5] max-w-md mx-auto">
              <AfricaMap className="w-full h-full" />
              {h.hero_image && (
                <div className="absolute -bottom-6 -left-6 w-44 h-44 lg:w-52 lg:h-52 rounded-3xl overflow-hidden border-4 border-white shadow-2xl">
                  <img
                    src={h.hero_image}
                    alt="African business professional"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              )}
              <div className="absolute -top-4 -left-4 sp-dot" />
              <div className="absolute top-1/3 -right-3 sp-dot" />
            </div>
          </div>
        </div>

        {/* Service icons */}
        {h.services?.length > 0 && (
          <div className="sp-container pb-16">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {h.services.map((s, i) => (
                <div
                  key={s.label + i}
                  className={`sp-card text-center sp-fade sp-fade-${(i % 5) + 1}`}
                  data-testid={`home-service-${i}`}
                >
                  <div
                    className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
                    style={{ background: `${s.color}14`, color: s.color }}
                  >
                    <Icon name={s.icon} size={28} strokeWidth={1.7} />
                  </div>
                  <div className="text-[13px] font-bold tracking-wide text-navy uppercase leading-tight">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* PIPELINE STRIP */}
      {h.pipeline?.length > 0 && (
        <section className="sp-strip">
          <div className="sp-container py-12">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 items-center">
              {h.pipeline.map((p, i) => (
                <div key={p.label + i} className="flex items-center gap-3" data-testid={`pipeline-${i}`}>
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-gold">
                    <Icon name={p.icon} size={24} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-white/60">Stage {i + 1}</div>
                    <div className="text-base font-extrabold text-white">{p.label}</div>
                  </div>
                  {i < h.pipeline.length - 1 && (
                    <Icon name="ArrowRight" size={20} className="text-gold ml-auto hidden sm:block" />
                  )}
                </div>
              ))}
            </div>
            {h.pipeline_caption && (
              <p className="mt-8 text-center text-white/85 max-w-3xl mx-auto">
                {h.pipeline_caption}
              </p>
            )}
          </div>
        </section>
      )}

      {/* VALUE PROPS */}
      {h.values?.length > 0 && (
        <section className="py-20">
          <div className="sp-container">
            <div className="max-w-2xl">
              <span className="sp-eyebrow">Why It Matters</span>
              <h2 className="mt-3 text-3xl lg:text-4xl font-extrabold">
                Built for Africa. <span className="text-gold">Engineered for the world.</span>
              </h2>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {h.values.map((v, i) => (
                <div key={v.title + i} className="sp-card" data-testid={`value-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-navy text-gold flex items-center justify-center">
                    <Icon name={v.icon} size={22} />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-navy">{v.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="pb-24">
        <div className="sp-container">
          <div className="rounded-3xl p-8 lg:p-12 bg-navy text-white relative overflow-hidden">
            <div className="absolute -right-12 -bottom-12 opacity-25 pointer-events-none">
              <AfricaMap className="w-80 h-80" />
            </div>
            <div className="relative grid md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="text-3xl lg:text-4xl font-extrabold">
                  Have an Idea? <span className="text-gold">Let's Build It.</span>
                </h3>
                <p className="mt-3 text-white/80 max-w-xl">
                  We turn bold ideas into scalable digital products and intelligent business systems.
                </p>
              </div>
              <div className="md:text-right">
                <Link to="/contact" className="sp-btn-gold" data-testid="home-bottom-cta">
                  <Icon name="Calendar" size={18} /> Schedule a Consultation
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className="sp-container py-32 text-center text-slate-500" data-testid="page-skeleton">
      Loading SiliconPay…
    </div>
  );
}
